import React, { useState, useEffect, useCallback } from 'react';
import { Form, Card, Alert } from 'react-bootstrap';
import { IoIosAlert } from 'react-icons/io';
import moment from 'moment';
import LoadingOverlay from '../../common/LoadingOverlay';
import Pagination from '../../../components/common/Pagination';
import ShopHOC from '../../../hoc/ShopHOC';
import api from '../../../api';
import handleClientSideSearch from '../../../utils/handleClientSideSearch';
import { useTranslation } from 'react-i18next';

import ProductListItem from './ProductListItem/ProductListItem';

import usePrevious from '../../../hooks/usePrevious';
import usePagination from '../../../hooks/usePagination';

function ProductList(props) {
    const { eventID, selectedKeys, portalClosed } = props;
    const [ filteredProductRows, setFilteredProductRows ] = useState([]);
    const [ productRows, setProductRows ] = useState([]);
    const [ filtersObj, setFilters ] = useState({ limit: 10, page: 1 });
    const [ loading, setLoading ] = useState(true);
    const [vat, setVat] = useState(true);
    const prevKeys = usePrevious({selectedKeys});
    const { t } = useTranslation();

    /**
     * Handle search.
     *
     * @param {string} search
     */
    const _handleSearchChange = (event) => {
        const search = event.target.value;
        setFilters({
            ...filtersObj,
            search: search
        })
    };

    /**
     * Handle sort.
     *
     * @param {object} event
     */
    const _handleSortChange = (event) => {
        setFilters({
            ...filtersObj,
            sorter: event.target.value
        })
    };

    const transformNumbers = (arr) => {
		return arr.map(obj => {
			return {
                ...obj,
                priceForSorting: Number(obj.totalPriceRounded)
            }
		})
	}

    /**
     * Handle sort.
     *
     * @param {object} event
     */
    const _handleRowsDisplayedChange = (event) => {
        setFilters({
            ...filtersObj,
            limit: event.target.value,
            page: 1
        })
    };

    const onVatChange = () => setVat(!vat);

     /**
     * Fetch products.
     *
     * @param {string} search
     */
    const _fetchProducts = useCallback((page) => {
        setLoading(true);

        api.shop.getProducts(eventID, {page: page ? page : 1})
            .then(response => {
                let { products } = response.data.data;

                products = products.filter(p => p.active).map((product, i) => {
                    if (product.bundledProducts) {
                        product.bundledProducts.forEach(bundledProduct => {
                            products.forEach(productForBundle => {
                                if (bundledProduct.productID === productForBundle.productID) {
                                    const price = bundledProduct.price;
                                    bundledProduct = Object.assign(bundledProduct, productForBundle);
                                    bundledProduct.price = Number(price).toFixed(2);
                                }
                            })
                        })
                    }
                    return product;
                });

                const { page } = response.data.meta;
                const [ key, direction ] = 'name_desc'.split('_');
                const desc = (direction === 'desc');
                const newRows = transformNumbers([...products]).sort((a,b) => {
                    if ( (desc && a[key] < b[key]) || (!desc && a[key] > b[key])) {
                        return -1;
                    }
                    if ( desc && a[key] > b[key] ) {
                        return 1;
                    }
                    return 0;
                });

                setProductRows(newRows);
                setFilteredProductRows(newRows);
                setFilters({
                    ...filtersObj,
                    page: page
                })
        })
        .finally(() => {
            setLoading(false);
        });

        return () => {
            setLoading(true);
            setProductRows([]);
            setFilteredProductRows([]);
            setFilters({
                ...filtersObj,
                page: 1
            })
        }
    }, [eventID, filtersObj]);

    const _handleFilters = useCallback(() => {
        let productRowsToBeFiltered = productRows;
        if (filtersObj.search && filtersObj.search.length) {
            // Filter product rows
            productRowsToBeFiltered = handleClientSideSearch(productRowsToBeFiltered, filtersObj.search)
        }

        if (filtersObj.sorter && filtersObj.sorter.length) {
            const [ key, direction ] = filtersObj.sorter.split('_');
            const desc = (direction === 'desc');
            productRowsToBeFiltered = transformNumbers([...productRowsToBeFiltered]).sort((a,b) => {
                if ( (desc && a[key] < b[key]) || (!desc && a[key] > b[key])) {
                    return -1;
                }
                if ( desc && a[key] > b[key] ) {
                    return 1;
                }
                return 0;
            });
        }

        if (filtersObj.selectedKeys && filtersObj.selectedKeys.length) {
            let productRowsToBeFilteredCache = [];
            productRowsToBeFiltered.forEach((productRow) => {
                let found = false;
                productRow.categories.forEach((category) => {
                    if (filtersObj.selectedKeys.includes(String(category.productCategoryId))) {
                        found = true;
                    }
                })
                if (found) {
                    productRowsToBeFilteredCache.push(productRow);
                }
            })
            productRowsToBeFiltered = productRowsToBeFilteredCache;
        }

        setFilteredProductRows(productRowsToBeFiltered);
    }, [filtersObj, productRows])


    const PortalClosedInfo = () => {
        const { timePartnerPortalOpens, timePartnerPortalCloses } = props.partnerPortalSettings;

        return (
            <div className="border-bottom">
                <Alert variant="warning" className="d-flex align-items-center ml-4 mr-4 mt-3">
                    <IoIosAlert size={20} style={{ marginRight: '5px'}} /> {t('general.portal_close_info')} {moment(timePartnerPortalOpens).format('DD.MM.YYYY HH:mm')} - {moment(timePartnerPortalCloses).format('DD.MM.YYYY HH:mm')}
                </Alert>
            </div>
        );
    };

    /**
     * Fetch products on component mount.
     */
    useEffect(_fetchProducts, [])

    useEffect(() => {
        _handleFilters()
    }, [_handleFilters])

    useEffect(() => {
        if (prevKeys && prevKeys.selectedKeys !== selectedKeys) {
            setFilters({
                ...filtersObj,
                selectedKeys: selectedKeys
            })
        }
    }, [prevKeys, selectedKeys, filtersObj, setFilters])

    const {
		setPage,
		pageCount,
		currentPage,
		currentData,
	} = usePagination(filteredProductRows, filtersObj.limit);

    const currentProducts = currentData();
    
    const _changeActivePage = ({ selected }) => setPage(selected + 1);

    return (
        <>
            {portalClosed ? 
                <PortalClosedInfo />
            : null}

            <div className="container DataListInputs">
                <div className="row justify-content align-items-center">
                    <div className="col col-sm-auto">
                        <select className="form-control form-control-sm" defaultValue={filtersObj.limit} onChange={_handleRowsDisplayedChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div className="col col-sm-auto">
                        <select className="form-control form-control-sm" defaultValue="name_desc" onChange={_handleSortChange}>
                            <option value="name_desc">{t('general.name')} (A-Z)</option>
                            <option value="name_asc">{t('general.name')} (Z-A)</option>
                            <option value="priceForSorting_desc">{t('products.price_desc')}</option>
                            <option value="priceForSorting_asc">{t('products.price_asc')}</option>
                        </select>
                    </div>
                    <div className="col col-sm-auto">
                        <input type="text" className="form-control form-control-sm" placeholder={t('general.search')} onChange={_handleSearchChange} />
                    </div>
                    <div className="col">
                        {filteredProductRows.length} {t('general.rows')}
                    </div>
                    <div className="col col-sm-auto">
                        <Form.Group controlId="tax" style={{ marginBottom: 0 }}>
                            <Form.Check
                                type="checkbox"
                                id="tax"
                                label={t('products.tax_included')}
                                value={vat}
                                defaultChecked={vat}
                                onChange={onVatChange}
                            />
                        </Form.Group>
                    </div>
                </div>
            </div>

            <div className="DataList">
                {currentProducts.map(product =>
                    <ProductListItem
                        vat={vat}
                        product={product}
                        eventID={props.eventID}
                        partnerPortalSettings={props.partnerPortalSettings}
                        key={product.productID}
                        addProductToCart={props.addProductToCart}
                        removeProductFromCart={props.removeProductFromCart}
                        portalClosed={portalClosed}
                    />
                )}
                {loading ?
                    <LoadingOverlay />
                : null}
            </div>

            {currentProducts.length ?
                <Card.Footer>
                    <div className="pull-right">
                        <Pagination
                            forcePage={currentPage}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={_changeActivePage}
                        />
                    </div>
                </Card.Footer>
            : null}

            {!currentProducts.length ?
                <Card.Body>
                    {t('products.no_products')}
                </Card.Body>
            : null}
        </>
    )
}

export default ShopHOC(ProductList);